import {
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
} from '@vue/composition-api'
import {
  apiToastError,
  apiToastSuccess,
} from '@core/utils/toast'
import { utils, constants as c } from '@kingpin-global/kingpin-utils-frontend'
import {
  debounce,
  elementInViewport,
  formatObject,
  getWishlistTitle,
} from '@core/utils/utils'
import { useResponsiveAppLeftSidebarVisibility } from '@core/comp-functions/ui/app'
import store from '@/store'
import analytics, {
  getSelectionOrWishlistMixpanelObj,
} from '@/@core/utils/analytics'
import constants, {
  PRODUCTS_LIMIT,
  searchBarPlaceholderText,
} from '@/constants'
import {
  FETCH_WISHLIST_FILTERS,
  FETCH_WISHLIST_PRODUCTS,
  UPDATE_CURRENT_CHECKOUT_WISHLIST,
  UPDATE_SELECT_COUNT,
  UPDATE_TOTAL_PRODUCTS_COUNT,
  FETCH_WISHLIST_PRODUCTS_GUEST_PRICED,
  FETCH_WISHLIST_FILTERS_GUEST,
  UPDATE_CART_ITEMS_COUNT,
} from '@/store/modules/shop.module'
import { useSelections } from '@/@core/comp-functions/selections/useSelection'
import { useEcommerceUi, useEcommerce } from '@/views/apps/useEcommerce'
import { useShopFiltersSortingAndPagination } from '@/views/apps/shop/useECommerceShop'
import { getColorCode } from '@/product-colors'

const { formatCurrency } = utils
const { BRAND_ACCESS } = c
const { CONNECTION_REQUEST_STATUS, TRACKS } = constants
const { MULTI_SELECT_PRODUCTS } = TRACKS.ACTIONS
// KP-3289: neutral swatch for colour families with no mapped colour, so every
// Color Family filter entry shows a dot (consistent across the wishlist).
const PUBLIC_FILTER_FALLBACK_COLOR = '#cbd5e1'

// KP-3289 public-wishlist facet helpers (module-level so buildPublicFilterOptions
// stays small). Operate on the raw product array returned by the guest endpoint.
const uniqFilterValues = values => [
  ...new Set(values.filter(v => v !== undefined && v !== null && v !== '')),
]

const numericMinMax = nums => (nums.length
  ? { min: Math.min(...nums), max: Math.max(...nums) }
  : { min: 0, max: 0 })

// Give every colour family a swatch so the filter reads consistently (no "some
// have a dot, some don't"): item hex → mapped colour for the name → fallback.
const buildPublicColorEntries = products => {
  const byFamily = {}
  products.forEach(product => (product.items || []).forEach(item => {
    if (item.colorFamily && !byFamily[item.colorFamily]) {
      byFamily[item.colorFamily] = {
        colorFamily: item.colorFamily,
        hexCode: item.hexCode
          || getColorCode(item.colorFamily)
          || PUBLIC_FILTER_FALLBACK_COLOR,
      }
    }
  }))
  return Object.values(byFamily)
}

// Build the ShopFilter facet options from the full set of raw products.
// Group-by dimensions for the public page. Labels MUST match the backend's
// Full canonical group-by list — the SAME set the server's getWishlistFilters
// returns and the V1 public wishlist renders (`Object.values(PRODUCT_FILTER_GROUPS)
// .map(LABEL)`): Departments, Categories, Sub Categories, Gender, Lines, Sub Lines,
// Group, Sub Groups, Key Styles. We list every dimension unconditionally (not gated
// on facet presence) for exact V1 parity — the public products endpoint groups
// server-side (groupProducts) by whichever label is selected, so each option works
// regardless of whether this wishlist happens to populate that field. 'None' leads
// as the ungrouped default the V2 dropdown expects.
const buildPublicGroupByOptions = () => {
  const labels = Object.values(c.PRODUCT_FILTER_GROUPS).map(group => group.LABEL)
  return ['None', ...labels.filter(label => label !== 'None')]
}

const buildPublicFilterOptions = products => {
  const retailPrices = products.map(p => p.retailPrice).filter(v => typeof v === 'number')
  const wholesalePrices = products
    .flatMap(p => (p.items || []).map(item => item.price))
    .filter(v => typeof v === 'number')
  const retail = numericMinMax(retailPrices)
  const wholesale = numericMinMax(wholesalePrices)
  const facets = {
    lines: uniqFilterValues(products.map(p => p.lines)),
    departments: uniqFilterValues(products.map(p => p.department)),
    categories: uniqFilterValues(products.map(p => p.category)),
    subCategories: uniqFilterValues(products.map(p => p.subCategory)),
    gender: uniqFilterValues(products.map(p => p.gender)),
    sizes: uniqFilterValues(products.flatMap(p => (p.items || []).map(item => item.size))),
    colorEntries: buildPublicColorEntries(products),
    retailPriceMin: retail.min,
    retailPriceMax: retail.max,
    // The wholesale slider is hidden for public (WSP is hidden), but
    // ShopFilter.updateFilters() always reads wholesalePrice[0..1], so the
    // bounds must exist to avoid a crash on any filter change.
    wholesalePriceMin: wholesale.min,
    wholesalePriceMax: wholesale.max,
  }
  // Group-by options — the full canonical list the server's getWishlistFilters
  // returns, so the public V2 dropdown offers every dimension V1 does.
  facets.groupBy = buildPublicGroupByOptions()
  return facets
}

/**
 * KP-4586: with a price source set, the page shows book prices while price
 * filters and sorts would still run on base prices, so the sliders go. The
 * min/max keys stay (undefined): ShopFilter reads them on every change.
 */
export const withoutPriceFacets = facets => ({
  ...facets,
  retailPriceMin: undefined,
  retailPriceMax: undefined,
  wholesalePriceMin: undefined,
  wholesalePriceMax: undefined,
})

/** Price sorts make no sense on a wishlist priced from a chosen book (KP-4586). */
export const sortOptionsFor = (options, hasPriceSource) => (hasPriceSource
  ? options.filter(option => !['lowest', 'highest'].includes(option.value))
  : options)

// Products array from the guest wishlist-products response (empty if absent).
// The public products endpoint returns grouped products ([{ _id, products }]).
// Facet derivation needs the raw flat product list, so flatten the groups.
// Falls back to treating an element as already-flat (a bare product) for safety.
const extractGuestWishlistProducts = res => {
  const groups = res?.data?.data?.wishlist?.products || []
  return groups.flatMap(g => (g && Array.isArray(g.products) ? g.products : [g]))
}

// Shape the public products response for the grid. The endpoint returns grouped
// products ([{ _id, products }]): one `_id: null` group for "None", or one group
// per value when grouped. Wrap each product as { productInfo } while keeping the
// group `_id` (drives the group headings). Falls back to a flat array (bare
// products) → a single group, for safety if the backend isn't updated.
const wrapPublicProducts = products => {
  const raw = Array.isArray(products) ? products : []
  const isGrouped = raw.length > 0 && raw[0] && Array.isArray(raw[0].products)
  if (isGrouped) {
    return raw.map(group => ({
      _id: group._id ?? null,
      products: (group.products || []).map(product => ({ productInfo: product })),
    }))
  }
  return [{ products: raw.map(product => ({ productInfo: product })) }]
}

const sortByOptions = useShopFiltersSortingAndPagination().sortByOptions

/** Page size of the branded line sheet's product loader (KP-4545). */
export const BRANDED_PAGE_LIMIT = 120

/**
 * One page of a public wishlist's products, flat and ungrouped (KP-4545).
 *
 * The branded line sheet groups products into its own sections, so it asks
 * the same guest endpoint the plain page uses for the plain product list and
 * buckets it itself. Same store action, same price stripping server-side.
 */
export const fetchPublicProductsPage = async (dispatch, publicToken, page, limit = BRANDED_PAGE_LIMIT) => {
  const res = await dispatch(FETCH_WISHLIST_PRODUCTS_GUEST_PRICED, {
    publicToken,
    payload: { page, limit, groupBy: 'None' },
  })
  const wishlist = res?.data?.data?.wishlist || {}
  return {
    wishlist,
    products: extractGuestWishlistProducts(res),
    count: Number(wishlist.count) || 0,
  }
}

/**
 * The retailer "opens a wishlist" events a public link fires, tagged with
 * which page the buyer saw: `branded` (KP-4545) or `plain` (today's page).
 */
export const trackPublicWishlistOpen = (data, variant) => {
  const props = formatObject(getSelectionOrWishlistMixpanelObj(data, false, { variant }))
  analytics.track(constants.TRACKS.PAGES.RETAILER_OPENS_WISHLIST_PRODUCTS, props)
  analytics.track(constants.TRACKS.ACTIONS.RETAILER_START_VIEWING_WISHLIST, props)
}

export { sortByOptions, searchBarPlaceholderText, formatCurrency }

export function useWishlistProducts(props, root) {
  const {
    parseProductImage,
    handleWishlistCartActionClick,
    toggleProductInWishlist,
    addMultipleItemsInCart,
    markItemsAsInCart,
  } = useEcommerceUi()
  const { mqShallShowLeftSidebar } = useResponsiveAppLeftSidebarVisibility()

  // KP-3289: the V2 public page derives its filter facets client-side (the
  // public facets endpoint 500s). Scope that strictly to the V2 route so the
  // legacy public page (which shares this composable) keeps its old behaviour.
  const isV2PublicWishlist = !!props.publicToken
    && !!root.$route
    && (root.$route.path || '').indexOf('/v2/') === 0

  // Reactive state
  const totalProducts = ref(0)
  const filters = ref({
    search: '',
    page: 0,
    limit: PRODUCTS_LIMIT,
  })
  const filterSetup = ref(false)
  // Bounded active retries for public facet hydration so it recovers on its own
  // (with backoff) instead of waiting for the next product fetch to re-trigger.
  let facetSetupAttempts = 0
  const MAX_FACET_SETUP_ATTEMPTS = 3
  const isLoaded = ref(false)
  const error = ref(null)
  const products = ref([])
  const filterOptions = ref({})
  let initialWholesalePriceMax
  let initialWholesalePriceMin
  let initialRetailPriceMax
  let initialRetailPriceMin
  const isWspHidden = ref(false)
  const requestStatus = ref(null)

  /**
   * Whether WSP may be shown to whoever is looking (KP-3861).
   *
   * A public link used to hide it from every recipient, full stop — that was
   * the only rule available (KP-3727), because a wishlist carried no setting of
   * its own. It carries one now and the brand may turn wholesale on, so this
   * defers to that instead of refusing outright.
   *
   * Turning it on is the only way through: the server strips the price from the
   * public payload whenever wholesale is hidden, so this can never reveal
   * anything the brand did not choose to send — it only stops the page throwing
   * away what was sent.
   *
   * Lives here because both the V1 and V2 wishlist pages render public links
   * and must agree.
   */
  const wspAllowedForViewer = computed(() => {
    if (!props.publicToken) return true

    return wishlist.value?.priceVisibility?.wholesale === true
  })
  const groups = ref({})
  const selectedGroup = ref('None')
  let productsCount = 0

  const isLoading = ref(false)
  const loadingIcon = ref(null)
  const continueLoading = ref(true)
  const isSearchApplied = ref(false)
  const collectionId = ref(null)

  const deselectedProducts = ref([])
  const fetchQueryParams = ref({})

  const wishlist = ref({})
  // KP-4586: the public link is priced from a chosen book.
  const hasPriceSource = computed(() => Boolean(props.publicToken && wishlist.value?.priceSource?.type))
  const isReadOnly = ref(false)
  const isBrand = computed(() => root.$store.getters.isBrand)
  const wishlistId = computed(() => root.$route.params.wishlistId)
  const isSelectedAllProducts = computed(
    () => root.$store.state.shop.isSelectedAllProducts,
  )
  const selectCount = computed(() => root.$store.state.shop.selectCount)
  const isPerformingAction = ref(false)
  const wishlistBaseCurrency = ref('')
  const indexes = ref([])

  // Fetch params
  const getFetchParams = () => {
    const fetchParam = {
      page: filters.value.page,
      wishlistId: wishlistId.value,
    }
    Object.keys(filters.value).forEach(key => {
      const value = filters.value[key]
      if (Array.isArray(value)) {
        // Skip empty arrays — they're not active filters and must not be sent as
        // query params (e.g. the display-only `colorEntries`, or untouched
        // facets mirrored as `[]` by processFilterOptions).
        if (value.length) fetchParam[key] = value.join(',')
      } else if (value) {
        fetchParam[key] = value
      }
    })
    fetchParam.groupBy = selectedGroup.value || 'None'
    return fetchParam
  }

  // Selection helpers
  const { onToggleSelectAll, isSelectedAllOnScreen } = useSelections(
    products,
    getFetchParams,
    indexes,
  )

  const forceToggleSelect = forceSelect => {
    onToggleSelectAll({ forceSelect })
  }

  const forceSelectAll = () => {
    onToggleSelectAll(true)
  }

  const toggleSelectAllHandler = () => {
    onToggleSelectAll({
      selectEventTrack: () => analytics.track(
        MULTI_SELECT_PRODUCTS.USER_CLICKS_SELECT_ALL_PRODUCTS_IN_WISHLIST_PRODUCTS_PAGE,
        { wishlist: wishlist.value },
      ),
      unselectEvent: () => analytics.track(
        MULTI_SELECT_PRODUCTS.USER_CLICKS_DESELECT_ALL_PRODUCTS_IN_WISHLIST_PRODUCTS_PAGE,
        { wishlist: wishlist.value },
      ),
    })
  }

  // Track visits
  const trackVisits = data => {
    if (isBrand.value) {
      analytics.track(
        constants.TRACKS.PAGES.BRAND_OPENS_SELECTION,
        formatObject(getSelectionOrWishlistMixpanelObj(data, isBrand.value)),
      )
      analytics.track(
        constants.TRACKS.ACTIONS.BRAND_START_VIEWING_SELECTION,
        formatObject(
          getSelectionOrWishlistMixpanelObj(wishlist.value, isBrand.value),
        ),
      )
      return
    }
    // KP-4545: a public link now opens either the branded page or this one;
    // tag the event so the two can be compared.
    const variantProps = props.publicToken ? { variant: 'plain' } : {}
    analytics.track(
      constants.TRACKS.PAGES.RETAILER_OPENS_WISHLIST_PRODUCTS,
      formatObject(getSelectionOrWishlistMixpanelObj(data, isBrand.value, variantProps)),
    )
    analytics.track(
      constants.TRACKS.ACTIONS.RETAILER_START_VIEWING_WISHLIST,
      formatObject(
        getSelectionOrWishlistMixpanelObj(wishlist.value, isBrand.value, variantProps),
      ),
    )
  }

  // Process response sub-functions
  const extractData = res => (props.publicToken ? res?.data?.data.wishlist : res?.data?.data)

  const updateTotalProductsCount = data => {
    const count = data?.count
    root.$store.commit(UPDATE_TOTAL_PRODUCTS_COUNT, count)
  }

  const shouldTrackVisits = isFilterApplied => !isFilterApplied && !isSearchApplied.value

  const processPublicTokenData = data => {
    if (props.publicToken) {
      data.products = wrapPublicProducts(data.products)
      isReadOnly.value = true
    } else if (data?.isReadOnly) {
      isReadOnly.value = data.isReadOnly
    }
  }

  const updateWishlistBaseCurrency = data => {
    wishlistBaseCurrency.value = data?.products?.[0]?.products?.[0]?.productInfo?.currency
  }

  const updateWishlistState = data => {
    collectionId.value = data.collectionId
    isWspHidden.value = Boolean(data.isWspHidden)
    requestStatus.value = data.connectionRequestStatus || null
  }

  const processProductColors = data => {
    data.products.forEach(groupedProduct => {
      groupedProduct.products.forEach(product => {
        const productColors = []
        const colorKeys = []
        productsCount++
        product.productInfo.items.forEach(item => {
          const key = `${item.colorFamily || ''}_${item.hexCode || ''}`
          const existingIndex = colorKeys.indexOf(key)
          if (existingIndex < 0) {
            colorKeys.push(key)
            productColors.push({
              colorNames: [item.color],
              colorFamily: item.colorFamily,
              hexCode: item.hexCode,
            })
          } else if (item.color && !productColors[existingIndex].colorNames.includes(item.color)) {
            productColors[existingIndex].colorNames.push(item.color)
          }
        })
        product.colors = productColors
      })
    })
  }

  const updateTotalProducts = data => {
    totalProducts.value = data.count
    continueLoading.value = data.count > productsCount && data.products.length
  }

  const appendProducts = data => {
    const lastGroup = products.value[products.value.length - 1]
    const firstNewGroup = data.products[0]
    if (data.products.length && lastGroup._id === firstNewGroup._id) {
      lastGroup.products = lastGroup.products.concat(firstNewGroup.products)
      data.products.splice(0, 1)
    }
    products.value = [...products.value, ...data.products]
  }

  const updateBreadcrumb = data => {
    if (!root.$route.meta.breadcrumb) return
    root.$route.meta.breadcrumb = root.$route.meta.breadcrumb.map(item => {
      if (item.dynamic && item.hasOwnProperty('wishlistName')) {
        item.wishlistName = data.name
      }
      return item
    })
  }

  const processRequestFetchWishlistProducts = (fetchParam, isFilterApplied) => res => {
    const data = extractData(res)
    updateTotalProductsCount(data)

    if (shouldTrackVisits(isFilterApplied)) {
      trackVisits(data)
    }

    processPublicTokenData(data)

    wishlist.value = data

    if (!fetchParam.currency) {
      updateWishlistBaseCurrency(data)
    }

    store.commit(UPDATE_CURRENT_CHECKOUT_WISHLIST, data)
    updateWishlistState(data)
    isLoaded.value = true

    if (filters.value.page === 0) {
      productsCount = 0
    }

    processProductColors(data)
    updateTotalProducts(data)

    if (filters.value.page >= 1) {
      appendProducts(data)
    } else {
      products.value = data.products
    }

    // KP-3289: derive the V2 public wishlist's filter facets from the first
    // (full, unfiltered) product load so ShopFilter can render for it.
    if (isV2PublicWishlist && !filterSetup.value) {
      setupPublicFilterOptions()
    }

    isLoading.value = false

    updateBreadcrumb(data)
  }

  const processErrorFetchWishlistProducts = err => {
    console.error(err)

    if (props.publicToken && err.response?.status === 404) {
      const errorMessage = err.response.data.message

      if (errorMessage === 'Wishlist is deleted by brand') {
        error.value = 'Wishlist is deleted by brand'
      } else if (errorMessage === 'No products found') {
        error.value = 'No products found'
      } else {
        error.value = 'Invalid link'
        apiToastError(err)
      }
    } else {
      apiToastError(err)
    }
  }

  // Fetch products
  const fetchWishlistProducts = async (
    fetchParam,
    isFilterApplied = false,
  ) => {
    let request = null
    fetchParam.wishlistId = wishlistId.value

    if (props.publicToken) {
      request = root.$store.dispatch(FETCH_WISHLIST_PRODUCTS_GUEST_PRICED, {
        publicToken: props.publicToken,
        payload: fetchParam,
      })
    } else {
      request = root.$store.dispatch(FETCH_WISHLIST_PRODUCTS, fetchParam)
    }

    request
      .then(processRequestFetchWishlistProducts(fetchParam, isFilterApplied))
      .catch(processErrorFetchWishlistProducts)
      .finally(() => {
        isLoaded.value = true
      })
    if (isFilterApplied || isSearchApplied.value) {
      analytics.track(
        isBrand.value
          ? constants.TRACKS.ACTIONS.BRAND_APPLIES_FILTER_SLN
          : constants.TRACKS.ACTIONS.RETAILER_APPLIES_FILTER_WLS,
        formatObject(fetchParam),
      )
    }
    fetchQueryParams.value = fetchParam
  }

  // Wrapper for fetchProducts
  const fetchShopProducts = newFilter => {
    let isFilterApplied = false

    if (newFilter && typeof newFilter === 'object') {
      isFilterApplied = true
      filters.value = newFilter
      filters.value.page = 0
      if (initialWholesalePriceMax === filters.value.wholesalePriceMax) delete filters.value.wholesalePriceMax
      if (initialWholesalePriceMin === filters.value.wholesalePriceMin) delete filters.value.wholesalePriceMin
      if (initialRetailPriceMax === filters.value.retailPriceMax) delete filters.value.retailPriceMax
      if (initialRetailPriceMin === filters.value.retailPriceMin) delete filters.value.retailPriceMin
    }
    const fetchParam = getFetchParams()
    fetchWishlistProducts(fetchParam, isFilterApplied)
  }

  // Remove product
  const { removeProductFromWishlist } = useEcommerce()
  const removeProductFromWishlistClick = product => {
    removeProductFromWishlist(product.productInfo._id, wishlistId.value).then(
      res => {
        apiToastSuccess(res.data.message || 'Removed product from wishlist!')
        products.value.forEach(groupedProduct => {
          const productIndex = groupedProduct.products.findIndex(
            p => p.productInfo._id === product.productInfo._id,
          )
          if (productIndex > -1) {
            groupedProduct.products.splice(productIndex, 1)
          }
        })
        totalProducts.value -= 1
        if (totalProducts.value === 0) {
          filterOptions.value = []
        }
        analytics.track(
          isBrand.value
            ? constants.TRACKS.ACTIONS.BRAND_REMOVES_SELECTED_ITEMS_FROM_SELECTION
            : constants.TRACKS.ACTIONS.RETAILER_REMOVES_PRODUCTS_FROM_WISHLIST,
          formatObject(
            getSelectionOrWishlistMixpanelObj(wishlist.value, isBrand.value, {
              selectedProductsCount: 1,
            }),
          ),
        )
      },
    )
    const fetchParam = {}
    fetchWishlistFilters(fetchParam)
  }

  // Toggle product in wishlist (bulk)
  const reactToggleProductInWishlist = async (
    product,
    fullIdsList,
    action,
  ) => {
    isPerformingAction.value = true
    let updatedProduct = await toggleProductInWishlist({
      product,
      wishlistId: wishlistId.value,
      action,
      wishlist: wishlist.value,
      fullIdsList,
    })
    store.commit(UPDATE_SELECT_COUNT, 0)
    updatedProduct = updatedProduct?.products || updatedProduct
    if (updatedProduct.length && !fullIdsList?.length) {
      updatedProduct.forEach(updatedProductData => {
        products.value.forEach(groupedProduct => {
          const productIndex = groupedProduct.products.findIndex(
            p => p.productId === updatedProductData.productId,
          )
          if (productIndex > -1) {
            groupedProduct.products.splice(productIndex, 1)
          }
        })
      })
      totalProducts.value -= updatedProduct.length
      if (totalProducts.value === 0) {
        filterOptions.value = []
      }
    } else {
      fetchShopProducts()
    }
    isPerformingAction.value = false
  }

  const selectedIndexesLength = ref(0)

  const updateWishlistProductsAfterCallback = updatedProducts => {
    if (Array.isArray(updatedProducts) && selectedIndexesLength) {
      updatedProducts.forEach(product => {
        product.isInCart = true
      })

      markItemsAsInCart(updatedProducts)
      store.commit(UPDATE_CART_ITEMS_COUNT, selectedIndexesLength.value)
    }
  }

  const addMultipleItemsCartActionClick = async ({ product, indexes: idxs }) => {
    selectedIndexesLength.value = idxs?.length
    await addMultipleItemsInCart({
      product,
      selectedProductsIds: idxs,
      location: 'wishlist',
      conflictResolveCallback: 'updateWishlistProductsAfterCallback',
      skipUpdateCartCount: true,
    })
    store.commit(UPDATE_SELECT_COUNT, 0)
    analytics.track(
      constants.TRACKS.ACTIONS.RETAILER_MOVES_FROM_WISHLIST_TO_CART,
      formatObject(getSelectionOrWishlistMixpanelObj(wishlist.value, false)),
    )
  }

  // Filters setup
  const addWishlistIdToFetchParam = fetchParam => {
    if (wishlistId.value) {
      fetchParam.wishlistId = wishlistId.value
    }
  }

  const createFetchWishlistFiltersRequest = fetchParam => {
    if (props.publicToken) {
      return root.$store.dispatch(FETCH_WISHLIST_FILTERS_GUEST, {
        publicToken: props.publicToken,
        payload: fetchParam,
      })
    }
    return root.$store.dispatch(FETCH_WISHLIST_FILTERS, fetchParam)
  }

  const processFilterOptions = () => {
    for (const key in filterOptions.value) {
      if (filterOptions.value.hasOwnProperty(key)) {
        filters.value[key] = []
        if (key.includes('PriceMax')) {
          filterOptions.value[key] = Math.round(filterOptions.value[key]) + 1
        }
        if (key.includes('PriceMin')) {
          filterOptions.value[key] = Math.round(filterOptions.value[key]) - 1
        }
      }
    }
  }

  const handleWishlistFiltersResponse = async request => {
    await request.then(res => {
      const facets = res.data.data[0] || {}
      filterOptions.value = hasPriceSource.value ? withoutPriceFacets(facets) : facets

      processFilterOptions()

      filterOptions.value.retailPrice = [
        filterOptions.value.retailPriceMin,
        filterOptions.value.retailPriceMax,
      ]
      filterOptions.value.wholesalePrice = [
        filterOptions.value.wholesalePriceMin,
        filterOptions.value.wholesalePriceMax,
      ]
      initialWholesalePriceMax = filterOptions.value.wholesalePriceMax
      initialWholesalePriceMin = filterOptions.value.wholesalePriceMin
      initialRetailPriceMax = filterOptions.value.retailPriceMax
      initialRetailPriceMin = filterOptions.value.retailPriceMin
      groups.value = filterOptions.value.groupBy
      filterSetup.value = true
    })
  }

  // The legacy public page asks for its filters before the first product page
  // says whether a price source is set, so drop the price facets once it does.
  watch(hasPriceSource, has => {
    if (has) filterOptions.value = withoutPriceFacets(filterOptions.value)
  })

  const fetchWishlistFilters = async fetchParam => {
    addWishlistIdToFetchParam(fetchParam)
    const request = createFetchWishlistFiltersRequest(fetchParam)
    await handleWishlistFiltersResponse(request)
  }

  // KP-3289: the public wishlist has no server facets endpoint, so derive the
  // ShopFilter options from the products (see buildPublicFilterOptions). The
  // grid loads paginated (PRODUCTS_LIMIT per page), so the facets are built from
  // a dedicated full-set fetch — not the first page — and computed once so the
  // facet lists stay stable while the user filters (which still re-queries the
  // public products endpoint server-side, like the authed flow).
  // Builds the full unfiltered product set for the client-side facets by paging
  // until a short page signals the end. This avoids sizing a single fetch by a
  // total count (which can be missing/late/wrong, or capped by the API) — it
  // pages at the standard PRODUCTS_LIMIT the API honours and stops on the first
  // partial page. Throws on fetch failure so setupPublicFilterOptions can retry;
  // an empty wishlist simply yields []. Capped to never loop on a bad endpoint.
  const fetchAllPublicProducts = async () => {
    // Pure non-termination guard (≈20k products) — NOT a product limit. The
    // short-page check below is the real terminator, so the full wishlist is
    // always fetched; this only stops a misbehaving endpoint looping forever.
    const PAGE_SAFETY_CEILING = 1000
    const all = []
    for (let page = 0; page < PAGE_SAFETY_CEILING; page += 1) {
      // eslint-disable-next-line no-await-in-loop
      const res = await root.$store.dispatch(FETCH_WISHLIST_PRODUCTS_GUEST_PRICED, {
        publicToken: props.publicToken,
        payload: { page, limit: PRODUCTS_LIMIT },
      })
      const pageProducts = extractGuestWishlistProducts(res)
      all.push(...pageProducts)
      if (pageProducts.length < PRODUCTS_LIMIT) break
    }
    return all
  }

  // KP-3360 — delivery-date (Filter By Delivery Date) options can't be derived
  // client-side: public wishlist products don't carry shipping dates. Pull the
  // `availabilityDates` windows from the public filters endpoint and merge them
  // into the client-built facets. Wrapped so a filters-endpoint failure can't
  // break the rest of the (client-derived) facets — the chip just shows no
  // options in that case. Applying a selection is handled server-side: the
  // `availabilityDates` filter flows through buildFetchParam to the products
  // endpoint, which the catalog-service already filters on.
  // Fetch the delivery-date windows for the public wishlist from the filters
  // endpoint. Returns a cleaned array (empty when none/unavailable).
  const fetchPublicDeliveryDateOptions = async () => {
    const res = await root.$store.dispatch(FETCH_WISHLIST_FILTERS_GUEST, {
      publicToken: props.publicToken,
      payload: { page: 0 },
    })
    const dates = res?.data?.data?.[0]?.availabilityDates
    return Array.isArray(dates) ? dates.filter(Boolean) : []
  }

  const setupPublicDeliveryDateOptions = async () => {
    try {
      const dates = await fetchPublicDeliveryDateOptions()
      if (!dates.length) return
      // Reassign the whole object so Vue 2 reactivity picks up the new
      // `availabilityDates` key (adding a key in place after an await is NOT
      // reactive in @vue/composition-api → the chip would stay hidden).
      filterOptions.value = { ...filterOptions.value, availabilityDates: dates }
      if (!Array.isArray(filters.value.availabilityDates)) {
        filters.value.availabilityDates = []
      }
    } catch (err) {
      // No delivery-date options available — leave the rest of the facets intact.
    }
  }

  const setupPublicFilterOptions = async () => {
    // Run at most MAX_FACET_SETUP_ATTEMPTS times: skip if already done/in-flight,
    // or once retries are exhausted — otherwise a failed setup would re-page the
    // whole wishlist on every later product fetch (scroll / search / filter).
    if (filterSetup.value || facetSetupAttempts >= MAX_FACET_SETUP_ATTEMPTS) return
    facetSetupAttempts += 1
    // Guard re-entry while the fetch is in flight; reset on failure (catch).
    filterSetup.value = true
    try {
      const allProducts = await fetchAllPublicProducts()
      const facets = buildPublicFilterOptions(allProducts)
      filterOptions.value = hasPriceSource.value ? withoutPriceFacets(facets) : facets
      // Feed the Group-by dropdown (mirrors the non-public path's
      // `groups.value = filterOptions.value.groupBy`).
      groups.value = filterOptions.value.groupBy
      processFilterOptions()
      filterOptions.value.retailPrice = [
        filterOptions.value.retailPriceMin,
        filterOptions.value.retailPriceMax,
      ]
      filterOptions.value.wholesalePrice = [
        filterOptions.value.wholesalePriceMin,
        filterOptions.value.wholesalePriceMax,
      ]
      initialRetailPriceMax = filterOptions.value.retailPriceMax
      initialRetailPriceMin = filterOptions.value.retailPriceMin
      initialWholesalePriceMax = filterOptions.value.wholesalePriceMax
      initialWholesalePriceMin = filterOptions.value.wholesalePriceMin
      await setupPublicDeliveryDateOptions()
    } catch (err) {
      // Clear so a retry can proceed; actively retry with backoff (until
      // attempts are exhausted) so facets recover without another product fetch.
      // The attempt was already counted at the top, so the guard above stops
      // both the timer and the product-fetch caller once exhausted.
      filterSetup.value = false
      if (facetSetupAttempts < MAX_FACET_SETUP_ATTEMPTS) {
        setTimeout(setupPublicFilterOptions, 1500 * facetSetupAttempts)
      }
    }
  }

  // The V2 public page derives facets from products (see
  // setupPublicFilterOptions, called after the first product fetch); everyone
  // else — including the legacy public page — fetches them from the server.
  if (!filterSetup.value && !isV2PublicWishlist) {
    const fetchParam = { page: 0 }
    fetchWishlistFilters(fetchParam)
  }

  // Currency filter
  const filterByCurrency = (currency = '') => {
    if (!currency) {
      delete filters.value?.currency
    } else {
      filters.value.currency = currency
      filters.value.page = 0
    }
    fetchShopProducts()
  }

  // Search
  const searchProducts = debounce(() => {
    isSearchApplied.value = true
    filters.value.page = 0
    fetchShopProducts()
  }, 1000)

  // Watch selectedGroup
  watch([selectedGroup], () => {
    filters.value.page = 0
    fetchShopProducts()
  })

  // Keep the body flag in lockstep with the *actual* sidebar state. ShopFilter's
  // backdrop closes the sheet by flipping mqShallShowLeftSidebar via .sync
  // without calling closeSideBar, so driving the class from a watcher (rather
  // than imperatively in open/close) guarantees it's removed on every close
  // path. The class lets the global scroll-to-top button hide while open.
  watch(mqShallShowLeftSidebar, isOpen => {
    if (typeof document === 'undefined') return
    document.body.classList.toggle('v2-wishlist-drawer-open', isOpen)
  })

  // Always clear the drawer body flag when the page using this composable
  // unmounts — covers every consumer (V2 + legacy WishlistProducts), so leaving
  // the page with the drawer open can't leave the scroll-to-top hidden globally.
  onUnmounted(() => {
    if (typeof document !== 'undefined') {
      document.body.classList.remove('v2-wishlist-drawer-open')
    }
  })

  // Sidebar close
  const closeSideBar = () => {
    mqShallShowLeftSidebar.value = false
  }

  // Sidebar open (mobile/tablet — the filter is an off-canvas sheet there,
  // opened from the Filter button in the controls bar).
  const openSideBar = () => {
    mqShallShowLeftSidebar.value = true
  }

  // Infinite scroll
  const onScrollLoader = () => {
    if (!isLoading.value && elementInViewport(loadingIcon.value)) {
      isLoading.value = true
      filters.value.page++
      fetchShopProducts()
    }
  }

  onMounted(() => {
    window.addEventListener('scroll', onScrollLoader)
  })

  onUnmounted(() => {
    window.removeEventListener('scroll', onScrollLoader)
  })

  // Cart conflict callback
  const updateProductAfterCartConflict = product => {
    if (product) {
      product.isInCart = true
      analytics.track(
        constants.TRACKS.ACTIONS.ADD_TO_CART,
        formatObject(product),
      )
      analytics.track(
        constants.TRACKS.ACTIONS.RETAILER_MOVES_FROM_WISHLIST_TO_CART,
        formatObject(getSelectionOrWishlistMixpanelObj(wishlist, false)),
      )
    }
  }

  return {
    // State
    wishlist,
    hasPriceSource,
    isLoaded,
    products,
    wishlistId,
    totalProducts,
    filters,
    filterOptions,
    filterSetup,
    isWspHidden,
    requestStatus,
    wspAllowedForViewer,
    groups,
    selectedGroup,
    continueLoading,
    loadingIcon,
    isLoading,
    isSearchApplied,
    collectionId,
    deselectedProducts,
    fetchQueryParams,
    isReadOnly,
    isPerformingAction,
    wishlistBaseCurrency,
    indexes,
    selectedIndexesLength,
    error,

    // Computed
    isBrand,
    isSelectedAllProducts,
    selectCount,

    // UI helpers
    parseProductImage,
    handleWishlistCartActionClick,
    updateProductAfterCartConflict,
    removeProductFromWishlistClick,
    toggleProductInWishlist,
    reactToggleProductInWishlist,
    addMultipleItemsInCart,
    addMultipleItemsCartActionClick,
    formatCurrency,
    mqShallShowLeftSidebar,
    fetchShopProducts,
    closeSideBar,
    openSideBar,
    searchProducts,
    filterByCurrency,
    forceSelectAll,
    onToggleSelectAll: toggleSelectAllHandler,
    isSelectedAllOnScreen,
    forceToggleSelect,
    updateWishlistProductsAfterCallback,
    getWishlistTitle,

    // Constants
    sortByOptions,
    searchBarPlaceholderText,
    CONNECTION_REQUEST_STATUS,
    BRAND_ACCESS,
  }
}
